import {
  INotification,
  notificationEnums,
} from "@freetech/models/notifications";

export const getAdminNotificationDescription = (
  notification: INotification
): string | undefined => {
  const { type, afterData, beforeData } = notification;

  switch (type) {
    case notificationEnums.AdminNotificationType.ADMIN_USER_CREATED:
      if (
        afterData &&
        "firstName" in afterData &&
        "lastName" in afterData &&
        "role" in afterData
      ) {
        return `${afterData.firstName} ${afterData.lastName} - Role: ${afterData.role}`;
      }
      return "New user added to the system";

    case notificationEnums.AdminNotificationType.ADMIN_USER_SIGNED_UP:
      if (
        afterData &&
        "firstName" in afterData &&
        "lastName" in afterData &&
        "signupDate" in afterData
      ) {
        const signupDate = new Date(afterData.signupDate as string);
        return `${afterData.firstName} ${afterData.lastName} - Signed up ${signupDate.toLocaleDateString()}`;
      }
      return "New user registration requires review";

    case notificationEnums.AdminNotificationType
      .ADMIN_USER_ONBOARDING_COMPLETE:
      if (
        afterData &&
        "firstName" in afterData &&
        "lastName" in afterData &&
        "role" in afterData
      ) {
        return `${afterData.firstName} ${afterData.lastName} completed ${afterData.role} onboarding`;
      }
      return "User has completed the onboarding process";

    case notificationEnums.AdminNotificationType.ADMIN_CLIENT_CREATED:
      if (
        afterData &&
        "contactEmail" in afterData &&
        "industry" in afterData
      ) {
        return `Contact: ${afterData.contactEmail} - Industry: ${afterData.industry}`;
      }
      return "New client added to the system";

    case notificationEnums.AdminNotificationType.ADMIN_CLIENT_UPDATED:
      if (afterData && beforeData) {
        const changes = [];
        if (
          "name" in afterData &&
          "name" in beforeData &&
          afterData.name !== beforeData.name
        ) {
          changes.push(`Name: ${beforeData.name} → ${afterData.name}`);
        }
        if (
          "contactEmail" in afterData &&
          "contactEmail" in beforeData &&
          afterData.contactEmail !== beforeData.contactEmail
        ) {
          changes.push(
            `Email: ${beforeData.contactEmail} → ${afterData.contactEmail}`
          );
        }
        if (changes.length > 0) {
          return changes.join(", ");
        }
      }
      return "Client information has been updated";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_CREATED:
      if (
        afterData &&
        "description" in afterData &&
        "estimatedHours" in afterData
      ) {
        return `Est. Hours: ${afterData.estimatedHours} - ${afterData.description}`;
      }
      return "New project added to the system";

    case notificationEnums.AdminNotificationType.ADMIN_PROJECT_UPDATED:
      if (
        afterData &&
        "status" in afterData &&
        beforeData &&
        "status" in beforeData
      ) {
        return `Status: ${beforeData.status} → ${afterData.status}`;
      }
      return "Project details have been updated";

    case notificationEnums.AdminNotificationType.ADMIN_CSA_CREATED:
      if (afterData && "value" in afterData && "duration" in afterData) {
        return `Value: $${afterData.value} - Duration: ${afterData.duration}`;
      }
      return "New consulting services agreement created";

    case notificationEnums.AdminNotificationType.ADMIN_CSA_SIGNED_BY_ADMIN:
      if (afterData && "signedDate" in afterData) {
        const signedDate = new Date(afterData.signedDate as string);
        return `Admin signed on ${signedDate.toLocaleDateString()}`;
      }
      return "CSA has been signed by admin";

    case notificationEnums.AdminNotificationType
      .ADMIN_CSA_SIGNED_BY_STAKEHOLDER:
      if (
        afterData &&
        "signedDate" in afterData &&
        "stakeholderName" in afterData
      ) {
        const signedDate = new Date(afterData.signedDate as string);
        return `${afterData.stakeholderName} signed on ${signedDate.toLocaleDateString()}`;
      }
      return "CSA has been signed by stakeholder";

    case notificationEnums.AdminNotificationType.ADMIN_INVOICE_CREATED:
      if (
        afterData &&
        "invoiceNumber" in afterData &&
        "dueDate" in afterData
      ) {
        const dueDate = new Date(afterData.dueDate as string);
        return `Invoice #${afterData.invoiceNumber} - Due ${dueDate.toLocaleDateString()}`;
      }
      return "New invoice generated";

    case notificationEnums.AdminNotificationType.ADMIN_INVOICE_PAID:
      if (
        afterData &&
        "invoiceNumber" in afterData &&
        "paidDate" in afterData
      ) {
        const paidDate = new Date(afterData.paidDate as string);
        return `Invoice #${afterData.invoiceNumber} - Paid ${paidDate.toLocaleDateString()}`;
      }
      return "Invoice payment received";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_CREATED:
      if (
        afterData &&
        "paymentMethod" in afterData &&
        "status" in afterData
      ) {
        return `Method: ${afterData.paymentMethod} - Status: ${afterData.status}`;
      }
      return "New payment initiated";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_STATUS_CHANGED:
      if (afterData && "statusChangeReason" in afterData) {
        return `Reason: ${afterData.statusChangeReason}`;
      }
      return "Payment status has been updated";

    case notificationEnums.AdminNotificationType.ADMIN_PAYMENT_COMPLETED:
      if (
        afterData &&
        "completedDate" in afterData &&
        "transactionId" in afterData
      ) {
        const completedDate = new Date(afterData.completedDate as string);
        return `Completed ${completedDate.toLocaleDateString()} - ID: ${afterData.transactionId}`;
      }
      return "Payment has been successfully processed";

    case notificationEnums.AdminNotificationType
      .ADMIN_PROJECT_ASSIGNMENT_CREATED:
      if (
        afterData &&
        "startDate" in afterData &&
        "estimatedHours" in afterData
      ) {
        const startDate = new Date(afterData.startDate as string);
        return `Start: ${startDate.toLocaleDateString()} - Est. Hours: ${afterData.estimatedHours}`;
      }
      return "New project assignment created";

    case notificationEnums.AdminNotificationType
      .ADMIN_PROJECT_ASSIGNMENT_SIGNED:
      if (afterData && "signedDate" in afterData) {
        const signedDate = new Date(afterData.signedDate as string);
        return `Signed on ${signedDate.toLocaleDateString()}`;
      }
      return "Project assignment has been accepted";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_CREATED:
      if (
        afterData &&
        "weekEnding" in afterData &&
        "projectName" in afterData
      ) {
        const weekEnding = new Date(afterData.weekEnding as string);
        return `Week ending ${weekEnding.toLocaleDateString()} - ${afterData.projectName}`;
      }
      return "New timesheet entry created";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_SUBMITTED:
      if (
        afterData &&
        "weekEnding" in afterData &&
        "submittedDate" in afterData
      ) {
        const weekEnding = new Date(afterData.weekEnding as string);
        const submittedDate = new Date(afterData.submittedDate as string);
        return `Week ending ${weekEnding.toLocaleDateString()} - Submitted ${submittedDate.toLocaleDateString()}`;
      }
      return "Timesheet submitted for approval";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_APPROVED:
      if (
        afterData &&
        "approvedDate" in afterData &&
        "approvedBy" in afterData
      ) {
        const approvedDate = new Date(afterData.approvedDate as string);
        return `Approved by ${afterData.approvedBy} on ${approvedDate.toLocaleDateString()}`;
      }
      return "Timesheet has been approved";

    case notificationEnums.AdminNotificationType.ADMIN_TIMESHEET_REJECTED:
      if (afterData && "rejectionReason" in afterData) {
        return `Reason: ${afterData.rejectionReason}`;
      }
      return "Timesheet requires revision";

    default:
      return undefined;
  }
};
